import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useEffect, useState } from "react";
import {
    LayoutDashboard,
    Car,
    ArrowLeftRight,
    Tags,
    Bell,
    ShieldCheck,
    CalendarClock,
    Upload,
    Users,
    LogOut,
    Menu,
    X,
    Landmark,
    BarChart3,
    ClipboardCheck,
    TrendingUp,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Badge } from "./ui/badge";

const NAV = [
    { to: "/inversion-inicial", label: "Inversión inicial", icon: Landmark },
    { to: "/resumen-ejecutivo", label: "Resumen Ejecutivo", icon: BarChart3 },
    { to: "/", label: "Resumen", icon: LayoutDashboard, end: true },
    { to: "/transacciones", label: "Ingresos y Gastos", icon: ArrowLeftRight },
    { to: "/autos", label: "Autos", icon: Car },
    { to: "/catalogos", label: "Categorías y Proveedores", icon: Tags },
    { to: "/seguros", label: "Seguros", icon: ShieldCheck },
    { to: "/vencimientos", label: "Vencimientos", icon: CalendarClock },
    { to: "/proyeccion", label: "Proyección", icon: TrendingUp },
    { to: "/recordatorios", label: "Recordatorios", icon: Bell },
    { to: "/historial-conductores", label: "Historial conductores", icon: ClipboardCheck },
    { to: "/importar", label: "Importar Excel", icon: Upload, viewerHidden: true },
    { to: "/usuarios", label: "Usuarios", icon: Users, adminOnly: true },
];

export default function Layout() {
    const [open, setOpen] = useState(false);
    const [notifCount, setNotifCount] = useState(0);
    const location = useLocation();
    const { user, isAdmin, isViewer, logout } = useAuth();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api.get("/notifications");
                setNotifCount(data.length);
            } catch (error) {
                if (process.env.NODE_ENV !== "production") {
                    console.error("No se pudieron cargar las notificaciones:", error);
                }
            }
        };
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [location.pathname]);

    return (
        <div className="min-h-screen flex bg-[#F8FAFC]">
            {/* Sidebar - desktop */}
            <aside className="hidden md:flex md:flex-col w-64 border-r border-slate-200 bg-white">
                <SidebarContent notifCount={notifCount} user={user} isAdmin={isAdmin} isViewer={isViewer} onLogout={logout} />
            </aside>

            {/* Mobile topbar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-slate-900 text-white grid place-items-center font-display font-semibold">C</div>
                    <span className="font-display font-semibold text-slate-900">Casas Tracking</span>
                </div>
                <button
                    data-testid="mobile-menu-toggle"
                    className="p-2 rounded-md hover:bg-slate-100"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Menú"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div className="md:hidden fixed inset-0 z-40 bg-black/30" onClick={() => setOpen(false)}>
                    <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <SidebarContent notifCount={notifCount} user={user} isAdmin={isAdmin} isViewer={isViewer} onLogout={logout} />
                    </aside>
                </div>
            )}

            {/* Content */}
            <main className="flex-1 md:ml-0 mt-14 md:mt-0">
                <div className={`mx-auto p-4 md:p-8 ${location.pathname === "/resumen-ejecutivo" ? "max-w-[1600px]" : "max-w-7xl"}`}>
                    <Outlet />
                </div>
            </main>
        </div>
    );
}

function SidebarContent({ notifCount, user, isAdmin, isViewer, onLogout }) {
    return (
        <div className="flex flex-col h-full">
            <div className="px-6 py-6 border-b border-slate-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-900 text-white grid place-items-center font-display font-semibold text-lg">C</div>
                    <div>
                        <div className="font-display font-semibold text-slate-900 leading-none">Casas Tracking</div>
                        <div className="text-xs text-slate-500 mt-1">Gestión de flotilla Uber</div>
                    </div>
                </div>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
                {NAV.filter((n) => (!n.adminOnly || isAdmin) && (!n.viewerHidden || !isViewer)).map(({ to, label, icon: Icon, end }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        data-testid={`nav-${to.replace("/", "") || "dashboard"}`}
                        className={({ isActive }) =>
                            `nav-link flex items-center justify-between gap-3 px-3 py-2 rounded-md text-sm ${
                                isActive
                                    ? "bg-slate-900 text-white"
                                    : "text-slate-700 hover:bg-slate-100"
                            }`
                        }
                    >
                        <span className="flex items-center gap-3">
                            <Icon size={18} />
                            {label}
                        </span>
                        {to === "/recordatorios" && notifCount > 0 && (
                            <Badge variant="secondary" className="bg-rose-100 text-rose-700 hover:bg-rose-100">
                                {notifCount}
                            </Badge>
                        )}
                    </NavLink>
                ))}
            </nav>
            {user && (
                <div className="border-t border-slate-200 p-3" data-testid="user-menu">
                    <div className="flex items-center gap-3 px-2 py-2">
                        <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center font-display font-semibold text-slate-700">
                            {user.name?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{user.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between px-2 mt-1">
                        <Badge className={isAdmin ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : isViewer ? "bg-sky-100 text-sky-700 hover:bg-sky-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                            {isAdmin ? "Administrador" : isViewer ? "Solo lectura" : "Empleado"}
                        </Badge>
                        <button
                            onClick={onLogout}
                            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600 px-2 py-1 rounded hover:bg-rose-50"
                            data-testid="logout-button"
                        >
                            <LogOut size={13} /> Salir
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
